import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  IconButton,
  Chip
} from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon
} from '@mui/icons-material';
import { useFavoritesListQuery, useToggleFavoriteMutation } from '../../services/salonsApi';
import { theme } from '../../theme';
import { buildMediaUrl } from '../../utils/urlHelpers';
import { useTranslation } from 'react-i18next';
import { getLocalizedField } from '../../utils/localizedFields';

const FavoritesList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: favorites, isLoading, refetch } = useFavoritesListQuery({ language: i18n.language });
  const [toggleFavorite] = useToggleFavoriteMutation();

  const handleRemoveFavorite = async (salonId, e) => {
    e.stopPropagation();
    try {
      await toggleFavorite(salonId).unwrap();
      refetch();
    } catch (error) {
      console.error(t('favorites.remove_error'), error);
    }
  };

  const handleSalonClick = (salonId) => {
    console.log("naviaget")
    navigate(`/salons/${salonId}`);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 4, 
            fontWeight: 700,
            color: theme.colors.text.primary
          }}
        >
          ❤️ {t('favorites.title')}
        </Typography>

        {!favorites || favorites.length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 8,
              backgroundColor: theme.colors.grey[50],
              borderRadius: 2
            }}
          >
            <FavoriteBorderIcon sx={{ fontSize: 80, color: theme.colors.grey[300], mb: 2 }} />
            <Typography variant="h6" sx={{ color: theme.colors.text.secondary, mb: 1 }}>
              {t('favorites.no_favorites')}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.colors.text.tertiary }}>
              {t('favorites.no_favorites_description')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {favorites.map((favorite) => {
              const salon = favorite.salon || favorite;
              const salonId = favorite.salon_id || salon.salon_id || salon.id;
              const salonName = getLocalizedField(salon, 'name', i18n.language) || 'Salon';
              const photoUrl = salon.photos && Object.keys(salon.photos).length > 0
                ? buildMediaUrl(Object.values(salon.photos)[0])
                : `https://via.placeholder.com/400x300?text=${encodeURIComponent(salonName)}`;

              return (
                <Grid item xs={12} sm={6} md={4} key={salonId}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.boxShadow.lg
                      }
                    }}
                    onClick={() => handleSalonClick(salonId)}
                  >
                    {/* Bouton Favoris */}
                    <IconButton
                      onClick={(e) => handleRemoveFavorite(salonId, e)}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(4px)',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 1)',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <FavoriteIcon sx={{ color: '#ef4444' }} />
                    </IconButton>

                    <CardMedia
                      component="img"
                      height="200"
                      image={photoUrl}
                      alt={salonName}
                      sx={{ objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 700,
                          color: theme.colors.primary[700],
                          mb: 1
                        }}
                      >
                        {salonName}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                        <LocationOnIcon sx={{ fontSize: '1rem', color: theme.colors.text.secondary, mt: 0.3 }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.colors.text.secondary,
                            fontSize: '0.875rem'
                          }}
                        >
                          {salon.formatted_address || salon.address || 'Adresse non disponible'}
                        </Typography>
                      </Box>

                      {salon.categories && salon.categories.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {salon.categories.slice(0, 2).map((category, index) => (
                            <Chip
                              key={index}
                              label={category.name || category}
                              size="small"
                              sx={{
                                backgroundColor: theme.colors.primary[50],
                                color: theme.colors.primary[700],
                                fontWeight: 600,
                                fontSize: '0.75rem'
                              }}
                            />
                          ))}
                          {salon.categories.length > 2 && (
                            <Chip
                              label={`+${salon.categories.length - 2}`}
                              size="small"
                              sx={{
                                backgroundColor: theme.colors.grey[100],
                                color: theme.colors.text.secondary,
                                fontSize: '0.75rem'
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default FavoritesList;
